import { Component, computed, input } from "@angular/core";
import { SharedModule } from "../../shared/shared.module";
import type { Artifact, LinkCard } from "../xwai.types";

/**
 * Renders the structured `artifacts` returned alongside an assistant message.
 *
 * Each artifact is a discriminated union keyed by `type`. The template switches
 * on `type` and IGNORES unknown types, so adding a new artifact kind is a
 * one-place change (a new `@case`/`@if` branch here) and an unknown type from
 * the backend never crashes the chat.
 *
 * Link cards are plain anchors with `target="_blank"` — always a new tab, handled
 * by the browser. No click interception, so middle-click and modifier-clicks keep
 * working natively too.
 */
@Component({
  selector: "app-xwai-artifacts",
  templateUrl: "./xwai-artifacts.component.html",
  styleUrls: ["./xwai-artifacts.component.less"],
  imports: [SharedModule],
})
export class XwaiArtifactsComponent {
  /** May be null/[]; both render nothing. */
  readonly artifacts = input<Artifact[] | null | undefined>(null);

  /** Non-null artifacts, ready to iterate in the template. */
  readonly items = computed<Artifact[]>(() => this.artifacts() ?? []);

  /**
   * Icon keys we know our ant-design set renders. An unrecognized `icon` is
   * omitted rather than passed to `nz-icon` (which would warn/error).
   */
  private static readonly KNOWN_ICONS = new Set([
    "fund",
    "table",
    "line-chart",
    "dot-chart",
    "bar-chart",
    "area-chart",
    "pie-chart",
    "heat-map",
    "link",
    "eye",
    "file-text",
    "appstore",
    "project",
    "experiment",
  ]);

  /** The icon name to render for a card, or null to omit it. */
  iconOf(card: LinkCard): string | null {
    const icon = card.icon;
    return icon && XwaiArtifactsComponent.KNOWN_ICONS.has(icon) ? icon : null;
  }
}
